import React, { useState } from "react";
import remoteApi from "../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Plus,
  Send,
  Loader2,
  FileText,
  MessageSquare,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Bell,
} from "lucide-react";

export default function NewTicket() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  async function handleCreate() {
    setLoading(true);
    try {
      const res = await remoteApi.createTicket({ subject, message });
      const payload = res.data || res;
      const ticketId = payload.ticket?.ticket_id || payload.ticket_id;
      navigate(`/tickets/${ticketId}`);
    } catch (err) {
      console.error("Failed to create ticket", err?.message || err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen p-6 pb-20 max-w-5xl mx-auto space-y-8"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/tickets")}
          className="p-3 bg-white hover:bg-gray-50 text-gray-600 rounded-xl shadow-sm hover:shadow-md transition-all group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        </button>
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {t("ticket.create_new_ticket", "Create Ticket")}
          </h1>
          <p className="text-gray-500 font-medium">
            {t("ticket.subtitle", "Submit a new support request")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2"
        >
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 overflow-hidden border border-gray-100">
            {/* Premium Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-12 bg-white/10 rounded-full blur-2xl -mr-6 -mt-6 pointer-events-none" />
              <div className="relative z-10 flex items-center gap-4">
                <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl border border-white/20 shadow-inner">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    {t("ticket.new_support_ticket", "New Support Ticket")}
                  </h3>
                  <p className="text-primary-100 text-sm font-medium opacity-90">
                    {t("ticket.fill_details", "Please detail your issue below")}
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="p-8 space-y-8">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreate();
                }}
                className="space-y-6"
              >
                {/* Subject Field */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <FileText className="w-4 h-4 text-primary-500" />
                    {t("ticket.subject")}
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={t("ticket.subject_placeholder")}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-medium text-gray-900 placeholder-gray-400"
                    required
                  />
                  <p className="text-xs text-gray-500 ml-1">
                    {t("ticket.subject_hint")}
                  </p>
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <MessageSquare className="w-4 h-4 text-primary-500" />
                    {t("ticket.detailed_description")}
                  </label>
                  <div className="relative">
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t("ticket.message_placeholder")}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-medium text-gray-900 placeholder-gray-400 resize-none min-h-[200px]"
                      rows={8}
                      required
                    />
                    <div className="absolute bottom-4 right-4 text-xs font-bold text-gray-400 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md">
                      {message.length} chars
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => navigate("/tickets")}
                    className="px-6 py-3 text-gray-600 font-bold hover:bg-gray-50 rounded-xl transition-all"
                  >
                    {t("ticket.cancel")}
                  </button>

                  <button
                    type="submit"
                    disabled={loading || !subject.trim() || !message.trim()}
                    className="bg-primary-600 hover:bg-primary-700 text-white font-bold px-8 py-3 rounded-xl shadow-lg shadow-primary-600/30 hover:shadow-xl hover:shadow-primary-600/40 hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t("ticket.creating", "Creating...")}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t("ticket.create_ticket_button", "Submit Ticket")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </motion.div>

        {/* Info Sidebar */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-1"
        >
          <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-lg relative overflow-hidden">
            {/* Decorative Background */}
            <div className="absolute inset-0 bg-primary-50/50" />
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary-100 rounded-full blur-3xl opacity-50" />

            <div className="relative z-10 space-y-6">
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  {t("ticket.what_happens_next")}
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("ticket.workflow_priority")}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white shadow-sm border border-primary-100 flex items-center justify-center text-primary-600">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-800 text-sm">
                      {t("ticket.workflow.submission_title")}
                    </h5>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("ticket.workflow.submission_desc")}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white shadow-sm border border-primary-100 flex items-center justify-center text-primary-600">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-800 text-sm">
                      {t("ticket.workflow.review_title")}
                    </h5>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("ticket.workflow.review_desc")}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white shadow-sm border border-primary-100 flex items-center justify-center text-primary-600">
                    <Bell className="w-5 h-5" />
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-800 text-sm">
                      {t("ticket.workflow.updates_title")}
                    </h5>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("ticket.workflow.updates_desc")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-primary-100">
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-xs text-primary-800 font-medium border border-primary-100">
                  {t("ticket.tip")}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
